<template>
  <BaseSidebar
    v-model="modelValue"
    :title="modalTitle"
    :icon="modalIcon"
    :description="modalDescription"
  >
    <div
      v-if="exception?.doctorStation.hasApprovedException"
      class="flex gap-2 text-left text-sm bg-primary5 border border-primary30 rounded-lg p-4"
    >
      <div>
        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="#2F7F91" viewBox="0 0 24 24">
          <path :d="mdiAlertCircleOutline" />
        </svg>
      </div>
      <p>
        Já existe outra excepcionalidade aprovada nessa vaga. Uma nova aprovação irá sobrepor as
        regras da anterior
      </p>
    </div>

    <div v-if="!exception" class="text-center py-8">
      <p class="text-textSecondary">Detalhes da exceção não disponíveis.</p>
    </div>
    <div v-else>
      <ExceptionDetailsCard
        class="mt-6"
        :request-date="exception.requestDate"
        :request-id="exception.id"
        :total-loss="Number(exception.financials.estimatedLossTotal)"
        :requester-name="exception.requester.name"
        :approved-exceptions="approvedExceptions"
        :rejected-exceptions="rejectedExceptions"
      />

      <ExceptionSimpleCard
        class="mt-4"
        title="Modalidade de repasse"
        :description="formatRepassMode(exception.changes.repassMode?.requested) || 'N/A'"
      />
      <ExceptionSimpleCard
        class="mt-4"
        title="Regras de proporcionalidade"
        :description="
          formatProportionalityRule(exception.changes.proportionalityRule?.requested) || 'N/A'
        "
      />

      <ExceptionWorkingDayCard
        v-if="
          exception.changes.workJourney?.requested?.startHour &&
          exception.changes.workJourney?.requested?.endHour
        "
        class="mt-4"
        :start-time="new Date(exception.changes.workJourney.requested.startHour)"
        :end-time="new Date(exception.changes.workJourney.requested.endHour)"
        :lost-hours="workJourneyLostHours"
        :estimated-loss="Number(exception.financials.estimatedLossTotal)"
      />

      <ExceptionSimpleCard
        class="mt-4"
        title="Justificativa"
        :description="exception.justification || 'Nenhuma justificativa fornecida.'"
      />
    </div>

    <template #footer>
      <div class="flex justify-around gap-4">
        <OutlinedButton
          text="Voltar"
          width="48%"
          class="mt-6 !bg-white"
          data-testid="exception-confirmation-back-button"
          @click="closeModal"
          height="48px"
        />
        <FilledButton
          :text="confirmButtonText"
          width="48%"
          :class="confirmButtonClasses"
          data-testid="exception-confirmation-confirm-button"
          @click="confirmAction"
          :loading="loading"
          height="48px"
        />
      </div>
    </template>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiLock, mdiAlertCircleOutline } from '@mdi/js'
import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ExceptionSimpleCard from '../Cards/ExceptionSimpleCard.vue'
import ExceptionDetailsCard from '../Cards/ExceptionDetailsCard.vue'
import ExceptionWorkingDayCard from '../Cards/ExceptionWorkingDayCard.vue'
import { differenceInHours } from 'date-fns'

import {
  ExceptionSummary,
  ModalActionType
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { formatProportionalityRule, formatRepassMode } from '@/utils/formatters'

const props = defineProps<{
  isOpen: boolean
  exception: ExceptionSummary | null
  loading: boolean
  actionType: ModalActionType
  approvedExceptions: string[]
  rejectedExceptions: string[]
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'confirmAction', type: ModalActionType): void
}>()

const modelValue = computed({
  get: () => props.isOpen,
  set: (value) => emit('update:isOpen', value)
})

const workJourneyLostHours = computed(() => {
  if (
    props.exception &&
    props.exception.changes.workJourney?.original &&
    props.exception.changes.workJourney?.requested
  ) {
    const originalStart = new Date(props.exception.changes.workJourney.original.startHour)
    const originalEnd = new Date(props.exception.changes.workJourney.original.endHour)
    const requestedStart = new Date(props.exception.changes.workJourney.requested.startHour)
    const requestedEnd = new Date(props.exception.changes.workJourney.requested.endHour)

    const originalDurationHours = differenceInHours(originalEnd, originalStart)
    const requestedDurationHours = differenceInHours(requestedEnd, requestedStart)
    const lost = originalDurationHours - requestedDurationHours
    return lost > 0 ? `${lost}h` : '0h'
  }
  return 'N/A'
})

const modalTitle = computed(() => {
  return props.actionType === ModalActionType.APPROVE
    ? 'Aprovar essa exceção?'
    : 'Reprovar essa exceção?'
})

const modalIcon = computed(() => {
  return props.actionType === ModalActionType.APPROVE ? undefined : mdiLock
})

const modalDescription = computed(() => {
  if (props.actionType === ModalActionType.APPROVE) {
    return 'Tem certeza que deseja aprovar esse pedido de excepcionalidade? Essa ação não poderá ser desfeita e afetará os atributos da vaga de plantão.'
  }
  return 'Tem certeza que deseja recusar esse pedido de excepcionalidade? Essa ação não poderá ser desfeita.'
})

const confirmButtonText = computed(() => {
  return props.actionType === ModalActionType.APPROVE ? 'Aprovar pedido' : 'Recusar pedido'
})

const confirmButtonClasses = computed(() => {
  return props.actionType === ModalActionType.APPROVE ? '!bg-primary30 mt-6' : '!bg-primary30 mt-6'
})

function closeModal() {
  emit('update:isOpen', false)
}

function confirmAction() {
  emit('confirmAction', props.actionType)
}
</script>
